// api gére les requetes vers l'api avec axios et la variable d'environnement;
import api from "../api";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

// Variables d'environnement pour l'URL de l'API
// const API_URL = process.env.REACT_APP_API_URL;

// Création d'un contexte d'authentification pour partager des informations avec les composants enfants
const AuthContext = createContext();

// Composant fournissant le contexte d'authentification et gérant l'état d'authentification
const AuthProvider = ({ children }) => {
  // Etat de chargement de l'authentification
  const [isLoading, setIsLoading] = useState(true);
  // Informations sur l'authentification JWT
  const [token, setToken_] = useState(localStorage.getItem("JWToken"));
  const [tokenRefresh, setTokenRefresh_] = useState(
    localStorage.getItem("JWTokenRefresh")
  );
  // Informations complementaires sur l'utilisateur
  const [userName, setUserName] = useState();
  const [userId, setUserId] = useState();
  const [isAdmin, setIsAdmin] = useState(false);

  // Initialisation du mode MJ
  if (localStorage.getItem("isMJ") === null) {
    localStorage.setItem("isMJ", "false");
  }
  const [isMJ, setIsMJ_] = useState(localStorage.getItem("isMJ") === "true");

  // Changement du mode MJ
  const setIsMJ = () => {
    setIsMJ_(!isMJ);
    localStorage.setItem("isMJ", isMJ ? "false" : "true");
  };

  // Définition des jetons d'authentifications JWT
  const setToken = (newToken) => {
    setToken_(newToken);
  };
  const setTokenRefresh = (newTokenRefresh) => {
    setTokenRefresh_(newTokenRefresh);
  };

  // Vérification de la validité du JWTokent
  const checkToken = async (tokenToCheck) => {
    return api
      .post("/auth/jwt/verify/", {
        token: tokenToCheck,
      })
      .then(() => {
        return true;
      })
      .catch((error) => {
        console.log("error checkToken: ", error);
        return false;
      });
  };

  // Définition du nom de l'utilisateur et des droits d'administrateur
  const setUser = async () => {
    try {
      const response = await api.get("/me/");
      setUserName(response.data.username);
      setUserId(response.data.id);
      setIsAdmin(response.data.is_admin);
    } catch (error) {
      console.log("error userName :", error);
      setUserName(null);
      setUserId(null);
      setIsAdmin(false);
    }
  };

  // Rafraichissement du JWToken
  const refreshToken = async () => {
    if (!tokenRefresh) {
      return;
    }

    return api
      .post("/auth/jwt/refresh/", {
        refresh: tokenRefresh,
      })
      .then((response) => {
        setToken(response.data.access);
      })
      .catch((error) => {
        console.log("error refereshToken: ", error);
        setToken(null);
      });
  };

  // Mise à jour des informations au changement du JWToken
  useEffect(() => {
    const verifyToken = async () => {
      // Loading le temps de vérifier/configurer le jeton
      setIsLoading(true);
      // Vérification de la validité du jeton
      if (token && (await checkToken(token))) {
        // Configuration des en-têtes axios avec le JWToken
        api.defaults.headers.common["Authorization"] = "JWT " + token;
        // Stockage des informations d'authentification dans le stockage local du navigateur
        localStorage.setItem("JWToken", token);
        localStorage.setItem("JWTokenRefresh", tokenRefresh);
        // Définition des paramètres de l'utilisateur
        await setUser();
      } else {
        // Suppression du jeton des en-têtes axios et du stockage local si le jeton est nul
        delete api.defaults.headers.common["Authorization"];
        localStorage.removeItem("JWToken");
        localStorage.removeItem("JWTokenRefresh");
        setUserName(null); // Réinitialisation du nom de l'utilisateur
        setUserId(null); // Retrait de l'id de l'utilisateur
        setIsAdmin(false); // Désactivation des droits d'administration
        setTokenRefresh(null);
        setToken(null);
      }
      setIsLoading(false);
    };
    verifyToken();
  }, [token]);

  // Rafraichissement du JWToken
  useEffect(() => {
    const refreshInterval = setInterval(() => {
      refreshToken();
    }, (4 * 60 * 60 + 55 * 60) * 1000); // Rafraichissement du JWToken toutes les 4 heures 55 minutes

    return () => clearInterval(refreshInterval);
  }, [tokenRefresh]);

  // Valeurs mémorisées du contexte d'authentification, utilisé pour passer des informations aux composants enfants
  const contextValue = useMemo(
    () => ({
      token,
      setToken,
      setTokenRefresh,
      userName,
      userId,
      isAdmin,
      isMJ,
      setIsMJ,
      isLoading,
    }),
    [token, userName, isAdmin, isMJ, isLoading]
  );

  // Fournit le contexte d'authentification aux composants enfants
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
};

// Exportation du composant permettant d'accéder au contexte d'authentification depuis n'importe quel composant
export const useAuth = () => {
  return useContext(AuthContext);
};

export default AuthProvider;
